/* ============================================
   TFS Wheels — Product Form Web Component
   Handles AJAX add-to-cart, variant selection,
   price updates, and URL state.
   ============================================ */

if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form[data-type="add-to-cart-form"]');
      this.submitButton = this.querySelector('[type="submit"]');
      this.variantIdInput = this.querySelector('[data-variant-id]');
      this.formQtyInput = this.querySelector('[data-form-qty]');
    }

    connectedCallback() {
      if (!this.form) return;

      this.form.addEventListener('submit', this.onSubmit.bind(this));

      // Listen to quantity input changes from <quantity-input>
      this.syncQuantity();

      // Set up variant selection
      this.initVariantPicker();
    }

    /* --- AJAX Add to Cart --- */
    async onSubmit(e) {
      e.preventDefault();

      if (this.submitButton.disabled || this.submitButton.classList.contains('loading')) return;

      this.submitButton.classList.add('loading');
      this.submitButton.setAttribute('aria-busy', 'true');

      const formData = new FormData(this.form);
      const body = {
        items: [
          {
            id: parseInt(formData.get('id')),
            quantity: parseInt(formData.get('quantity')) || 4
          }
        ]
      };

      try {
        await CartAPI.add(body.items);
        // PubSub.publish('cart:updated') is called inside CartAPI.add
        this.showSuccess();
      } catch (error) {
        this.showError(error.message || 'Could not add to cart. Please try again.', error.partial);
      } finally {
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-busy');
      }
    }

    showSuccess() {
      const btnText = this.submitButton.querySelector('.btn-text');
      if (!btnText) return;

      const originalText = btnText.textContent;
      const icon = btnText.querySelector('svg');

      // Temporarily show success
      btnText.textContent = 'Added!';
      if (icon) {
        btnText.prepend(icon);
      }
      this.submitButton.classList.add('btn--success');

      setTimeout(() => {
        btnText.textContent = originalText;
        if (icon) {
          btnText.prepend(icon);
        }
        this.submitButton.classList.remove('btn--success');
      }, 2000);
    }

    showError(message, partial) {
      // Floating toast (global.js) — an inline element here pushed and
      // overlapped the buy-box rows below the form.
      TFSToast.show(message, { tone: partial ? 'warning' : 'error' });
    }

    /* --- Quantity Sync --- */
    syncQuantity() {
      // Watch for changes on the quantity input from <quantity-input>
      const section = this.closest('[data-section-id]');
      if (!section) return;

      const qtyInput = section.querySelector('quantity-input input[name="quantity"]');
      if (qtyInput && this.formQtyInput) {
        // Initial sync
        this.formQtyInput.value = qtyInput.value;

        qtyInput.addEventListener('change', () => {
          this.formQtyInput.value = qtyInput.value;
        });
      }
    }

    /* --- Variant Picker --- */
    initVariantPicker() {
      const section = this.closest('[data-section-id]');
      if (!section) return;

      const sectionId = section.dataset.sectionId;
      this.variantsJSON = this.getVariantsJSON(sectionId);
      if (!this.variantsJSON || this.variantsJSON.length === 0) return;

      this.product = {
        variants: this.variantsJSON
      };

      // Listen to variant button clicks
      section.querySelectorAll('.pdp-variant-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const group = btn.closest('.pdp-variant-buttons');
          group.querySelectorAll('.pdp-variant-btn').forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          this.onVariantChange(section, sectionId);
        });
      });

      // Listen to variant select changes
      section.querySelectorAll('.pdp-variant-select').forEach(select => {
        select.addEventListener('change', () => {
          this.onVariantChange(section, sectionId);
        });
      });
    }

    getVariantsJSON(sectionId) {
      const el = document.getElementById('ProductJSON-' + sectionId);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (err) {
        return null;
      }
    }

    onVariantChange(section, sectionId) {
      const selectedOptions = this.getSelectedOptions(section);
      const variant = this.findVariant(selectedOptions);

      if (!variant) return;

      // Update hidden variant ID
      if (this.variantIdInput) {
        this.variantIdInput.value = variant.id;
      }

      // Update price display
      this.updatePrice(section, variant);

      // Update button state
      this.updateButton(variant);

      // Update URL
      this.updateURL(variant);

      // Update media if variant has associated image
      this.updateMedia(section, sectionId, variant);

      // Publish event
      PubSub.publish('variant:changed', { variant, sectionId });
    }

    getSelectedOptions(section) {
      const options = [];

      // Button groups
      section.querySelectorAll('.pdp-variant-buttons').forEach(group => {
        const active = group.querySelector('.pdp-variant-btn.active');
        if (active) options.push(active.dataset.value);
      });

      // Dropdowns
      section.querySelectorAll('.pdp-variant-select').forEach(select => {
        options.push(select.value);
      });

      return options;
    }

    findVariant(selectedOptions) {
      return this.product.variants.find(variant => {
        return variant.options.every((option, index) => {
          return option === selectedOptions[index];
        });
      });
    }

    updatePrice(section, variant) {
      const priceEl = section.querySelector('[data-product-price]');
      const setEl = section.querySelector('[data-set-price]');
      const compareEl = section.querySelector('.pdp-price-compare');
      const saleBadge = section.querySelector('[data-badge-sale]');
      const discountBox = section.querySelector('[data-discount-box]');
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

      if (priceEl) {
        priceEl.textContent = formatMoney(variant.price);
        priceEl.classList.toggle('pdp-price-current--sale', onSale);
      }

      if (setEl) {
        const setParent = setEl.closest('.pdp-price-set');
        const setQty = setParent && setParent.dataset.setQty ? parseInt(setParent.dataset.setQty) : 4;
        setEl.textContent = formatMoney(variant.price * setQty);
      }

      // Installment teaser: set total ÷ 4 (Pay-in-4). Set qty from data attr (default 4).
      const installmentEl = section.querySelector('[data-installment]');
      if (installmentEl) {
        const instWrap = installmentEl.closest('.pdp-installments');
        const instSetQty = instWrap && instWrap.dataset.setQty ? parseInt(instWrap.dataset.setQty) : 4;
        installmentEl.textContent = formatMoney(Math.floor(variant.price * instSetQty / 4));
      }

      if (compareEl) {
        if (onSale) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }

      // Toggle sale badge + refresh percent on variant change
      if (saleBadge) {
        saleBadge.style.display = onSale ? '' : 'none';
        if (onSale) {
          const pctEl = saleBadge.querySelector('[data-badge-sale-pct]');
          if (pctEl) {
            const pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
            pctEl.textContent = '−' + pct + '% off!';
          }
        }
      }

      // Hide discount code when variant is already on sale
      if (discountBox) {
        discountBox.style.display = (onSale || !variant.available) ? 'none' : '';
      }
    }

    updateButton(variant) {
      if (!this.submitButton) return;

      const btnText = this.submitButton.querySelector('.btn-text');
      if (!btnText) return;

      const icon = btnText.querySelector('svg');

      if (variant.available) {
        this.submitButton.disabled = false;
        btnText.textContent = ' Add to Cart';
        if (icon) btnText.prepend(icon);
      } else {
        this.submitButton.disabled = true;
        btnText.textContent = ' Sold Out';
        if (icon) btnText.prepend(icon);
      }
    }

    updateURL(variant) {
      if (!variant) return;
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    updateMedia(section, sectionId, variant) {
      if (!variant.featured_media) return;

      // Try to find the matching thumbnail and click it
      const mediaId = variant.featured_media.id;
      const thumb = section.querySelector('.pdp-thumb[data-media-id="' + mediaId + '"]');
      if (thumb && !thumb.classList.contains('active')) {
        thumb.click();
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
